import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { Outlet } from 'react-router-dom';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineM, LabelM } from '@sorare/core/src/atoms/typography';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import {
  ANY_SPORT_PLAY,
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_REWARDS,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { fantasy, navLabels } from '@sorare/core/src/lib/glossary';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { EventTypeSwitcher } from 'components/fixture/EventTypeSwitcher';
import { BasicFixtureLayout } from 'layouts/BasicFixtureLayout';
import { ResponsiveTitle } from 'layouts/FixtureLayout/ResponsiveTitle';
import { getFixtureQueryVariables, isFixtureOpened } from 'lib/so5';

import type {
  FixtureRewardsLayout_So5Fixture,
  PlayRewardsFixtureLayoutQuery,
  PlayRewardsFixtureLayoutQueryVariables,
} from './__generated__/layout.graphql';

const PLAY_REWARDS_FIXTURE_LAYOUT_QUERY = gql`
  query PlayRewardsFixtureLayoutQuery(
    $type: So5State
    $slug: String
    $sport: Sport
  ) {
    so5 {
      so5Fixture(type: $type, slug: $slug, sport: $sport) {
        slug
        displayName
        type
        shortDisplayName
        mySo5LeaderboardContenders {
          nodes {
            slug
          }
        }
        ...isFixtureOpened_so5Fixture
      }
    }
  }
  ${isFixtureOpened.fragments.so5Fixture}
` as TypedDocumentNode<
  PlayRewardsFixtureLayoutQuery,
  PlayRewardsFixtureLayoutQueryVariables
>;

const Header = styled(FullWidthContainer)`
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  background: var(--c-black);
  padding: var(--triple-unit) var(--container-padding);

  @media ${laptopAndAbove} {
    min-height: calc(
      11 * var(--unit)
    ); /** height of the buttons on the side to avoid layout shift */
    grid-template-columns: repeat(3, 1fr);

    > * {
      flex: 1;
    }
  }
`;

const StyledVertical = styled(Vertical)`
  align-items: start;
`;

const BottomButtonWrapper = styled(Vertical).attrs({ center: true })`
  margin-bottom: var(--double-unit);
`;

const PaddedFixtureLayout = styled(BasicFixtureLayout)`
  &.no-padding-top {
    padding-top: 0;
  }

  @media ${laptopAndAbove} {
    padding-top: 0;
  }
`;

export const loaderFn = <
  Params extends {
    eventType?: string;
    fixture?: string;
  },
>() =>
  queryLoader<Params>(
    PLAY_REWARDS_FIXTURE_LAYOUT_QUERY,
    ({ sport, eventType, fixture }) => {
      return {
        ...getFixtureQueryVariables(fixture),
        sport,
        eventType: eventType?.toUpperCase(),
      };
    },
    { preventTransitionBeforeLoaded: true }
  );

export const FixtureRewardsLayout = () => {
  const {
    flags: { useMlbNewPlayPage = false },
  } = useFeatureFlags();
  const { sport, generateSportContextPath } = useSportContext();
  const isLaptopAndAbove = useIsLaptop();
  const { data } = useQueryLoader<PlayRewardsFixtureLayoutQuery>();
  const {
    so5: { so5Fixture },
  } = data;

  if (!so5Fixture || !sport) {
    return null;
  }

  const isMyLineupsTab =
    sport === Sport.FOOTBALL || (useMlbNewPlayPage && sport === Sport.BASEBALL);

  const pageHeaderProps = isMyLineupsTab
    ? {
        text: navLabels.myLineups,
        disableBackBrowser: true,
      }
    : {
        customTitle: (
          <StyledVertical gap={0}>
            <HeadlineM as="h1" bold>
              <FormattedMessage {...navLabels.lobby} />
            </HeadlineM>
            {!isLaptopAndAbove && (
              <LabelM as="p" color="var(--c-nd-600)">
                {so5Fixture.shortDisplayName}
              </LabelM>
            )}
          </StyledVertical>
        ),
        backTo: generateSportContextPath(ANY_SPORT_PLAY),
      };

  const eventTypeSwitcher = sport !== Sport.FOOTBALL && (
    <EventTypeSwitcher
      currentEventType={so5Fixture.type}
      baseRoute={ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_REWARDS}
    />
  );

  return (
    <>
      <PageHeader {...pageHeaderProps} />
      <FullWidthContainer.NoPadding>
        <Header>
          <>
            {isLaptopAndAbove ? (
              <div>{eventTypeSwitcher}</div>
            ) : (
              <Portal id="above-bottom-bar-portal">
                <BottomButtonWrapper>{eventTypeSwitcher}</BottomButtonWrapper>
              </Portal>
            )}
            <ResponsiveTitle>
              <FormattedMessage
                {...(isFixtureOpened(so5Fixture)
                  ? fantasy.prizePool
                  : fantasy.leaderboard)}
              />
            </ResponsiveTitle>
          </>
        </Header>
        <PaddedFixtureLayout
          className="no-padding-top"
          eventType={so5Fixture.type}
          navigateTo={fixture =>
            generateSportContextPath(ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_REWARDS, {
              sport,
              params: {
                eventType: so5Fixture.type.toLowerCase(),
                fixture,
              },
            })
          }
          pickerSize="big"
        >
          <Outlet
            context={{
              isUpcoming: isFixtureOpened(so5Fixture),
            }}
          />
        </PaddedFixtureLayout>
      </FullWidthContainer.NoPadding>
    </>
  );
};

FixtureRewardsLayout.fragments = {
  So5Fixture: gql`
    fragment FixtureRewardsLayout_So5Fixture on So5Fixture {
      slug
      type
      mySo5LeaderboardContenders {
        nodes {
          slug
        }
      }
      ...isFixtureOpened_so5Fixture
    }
    ${isFixtureOpened.fragments.so5Fixture}
  ` as TypedDocumentNode<FixtureRewardsLayout_So5Fixture>,
};
